import {EntityType, isEntityType} from "./model/entity";

/** Where an entity's profile lives in the app and which permission prefix gates viewing it. */
export type EntityLinkConfig = {
  link?: string;
  permissionPrefix: string;
};

const DEFAULT_ENTITY_LINK_CONFIG: EntityLinkConfig = {
  link: undefined,
  permissionPrefix: "",
};


const ENTITY_LINK_CONFIG_OVERRIDES: Partial<Record<EntityType, EntityLinkConfig>> = {
  SITE: {link: 'sites', permissionPrefix: 'site'},
  WELL: {link: 'wells', permissionPrefix: 'well'},
  CONTAINER: {link: 'containers', permissionPrefix: 'container'},
  CONCOMMIGLED: {link: 'container-clusters', permissionPrefix: 'containercluster'},
  ROUTE: {link: 'routes', permissionPrefix: 'route'},
  PIPE: {link: 'pipelines', permissionPrefix: 'pipeline'},
  METER: {link: 'meters', permissionPrefix: 'meter'},
  VEHICLE: {link: 'vehicles', permissionPrefix: 'vehicle'},
  TRAILER: {link: 'trailers', permissionPrefix: 'trailer'},
  PERSON: {link: 'persons', permissionPrefix: 'person'},
  PERSONCAP: {link: undefined, permissionPrefix: ""},
  ABAC_POLICY: {link: 'abac-policies', permissionPrefix: 'abacpolicy'},
  PERMISSION: {link: 'permissions', permissionPrefix: 'permission'},
  ROLE: {link: 'roles', permissionPrefix: 'role'},
  RIG: {link: undefined, permissionPrefix: "rig"},
  DRIVERLOCATION: {link: undefined, permissionPrefix: "driverlocation"},
  TRANSPORT: {link: undefined, permissionPrefix: "transport"},
  GEOFENCEEVENT: {link: undefined, permissionPrefix: "geofenceevent"},
  TRANSPORTTRANSPORTASSOC: {link: "tickets", permissionPrefix: "transporttransportassoc"},
  LEGACYPIPELINEFLOW: {link: undefined, permissionPrefix: ""},
  REGION: {link: "regions", permissionPrefix: "region"},
  COMPANY: {link: "companies", permissionPrefix: "company"},
  LEGACYPIPELINE: {link: undefined, permissionPrefix: ""},
  LEGACYPIPELINEMETER: {link: undefined, permissionPrefix: ""},
  OPERATINGAREA: {
    link: "operating-areas",
    permissionPrefix: "operatingarea",
  },
  FREIGHTTYPE: {
    link: undefined,
    permissionPrefix: ""
  },
  WELLSTAGE: {
    link: undefined,
    permissionPrefix: ""
  },
  WELLSTAGES: {
    link: undefined,
    permissionPrefix: ""
  },
  SITEASSIGN: {
    link: undefined,
    permissionPrefix: ""
  },
  SITEASSIGNCOMPASSOC: {
    link: undefined,
    permissionPrefix: ""
  },
  SITEASSIGNSITEASSOC: {
    link: undefined,
    permissionPrefix: ""
  },
  SITE_RESTRICTION: {
    link: undefined,
    permissionPrefix: ""
  },
  RATEMODEL: {
    link: undefined,
    permissionPrefix: ""
  },
  WATERTYPE: {
    link: undefined,
    permissionPrefix: ""
  },
  SERVICETYPE: {
    link: "service-types",
    permissionPrefix: "servicetype"
  },
  SERVICEWATERASSOC: {
    link: undefined,
    permissionPrefix: ""
  },
  DISPATCHREQUEST: {
    link: "requests",
    permissionPrefix: "dispatchrequest"
  },
  MESSAGE: {
    link: undefined,
    permissionPrefix: ""
  },
  DISPATCH: {
    link: undefined,
    permissionPrefix: ""
  },
  ROUTEGROUP: {
    link: undefined,
    permissionPrefix: ""
  },
  PIPELINEGRAPH: {
    link: undefined,
    permissionPrefix: ""
  },
  PIPELINEGRAPHEDGE: {
    link: undefined,
    permissionPrefix: ""
  },
  PIPELINEGRAPHNODE: {
    link: undefined,
    permissionPrefix: ""
  },
  PIPE_FLOW: {
    link: undefined,
    permissionPrefix: "pipelineflow"
  },
  PIPE_FLOW_TRANSPORT: {
    link: undefined,
    permissionPrefix: ""
  },
  METER_FLOW_READING: {
    link: undefined,
    permissionPrefix: ""
  },
  UNITOFMEASURE: {
    link: undefined,
    permissionPrefix: ""
  },
  SERVICETYPERULE: {
    link: undefined,
    permissionPrefix: ""
  },
  GEOFENCE: {
    link: undefined,
    permissionPrefix: ""
  },
  SHIFT: {
    link: "shifts",
    permissionPrefix: "shift"
  },
  ALLOCATABLE_LOAD_SETTINGS: {
    link: undefined,
    permissionPrefix: ""
  },
  ALLOCATABLE_LOAD: {
    link: undefined,
    permissionPrefix: ""
  },
  PROD_VOL: {
    link: undefined,
    permissionPrefix: ""
  },
  METER_PROD_VOL_READING: {
    link: undefined,
    permissionPrefix: ""
  },
  SITESITEASSOC: {
    link: undefined,
    permissionPrefix: ""
  },
  METER_FLOW_VOLUME: {
    link: undefined,
    permissionPrefix: ""
  },
  OBJECTDOCASSOC: {
    link: undefined,
    permissionPrefix: ""
  },
  OBJECTPICASSOC: {
    link: undefined,
    permissionPrefix: ""
  },
  INVOICE: {
    link: "invoices",
    permissionPrefix: "invoice"
  },
  TLI: {
    link: undefined,
    permissionPrefix: ""
  },
  TICKETCODE: {
    link: undefined,
    permissionPrefix: ""
  },
  SETTLEMENT: {
    link: "settlements",
    permissionPrefix: "settlement"
  },
  COMPANY_SETTLEMENT: {
    link: undefined,
    permissionPrefix: ""
  },
  ACTIVITYPERIOD: {
    link: undefined,
    permissionPrefix: ""
  },
  OBJECT_PREF: {
    link: undefined,
    permissionPrefix: ""
  },
  WATERGO_PREF: {
    link: undefined,
    permissionPrefix: ""
  },
  OILGO_PREF: {
    link: undefined,
    permissionPrefix: ""
  },
  COMPANY_UNIT: {
    link: undefined,
    permissionPrefix: ""
  },
  TRANSPORTGAUGE: {
    link: undefined,
    permissionPrefix: ""
  },
  INVACCTLOOKUP: {
    link: undefined,
    permissionPrefix: ""
  },
  CONVOLDROP: {
    link: undefined,
    permissionPrefix: ""
  },
  DVIR: {
    link: "dvirs",
    permissionPrefix: "dvir"
  },
  GEOSITEGROUP: {
    link: "geofence-site-groups",
    permissionPrefix: "geofencesitegroup"
  },
  RRCLOCPROFILE: {
    link: undefined,
    permissionPrefix: "rrclocationprofile"
  },
  RRCCOMPANYPROFILE: {
    link: undefined,
    permissionPrefix: "rrccompanyprofile"
  },
  RRCEP9WASTEPROFILE: {
    link: "compliance/rrc/ep10",
    permissionPrefix: "rrcep9wasteprofile"
  },
  RRCEP10WASTEMFST: {
    link: "compliance/rrc/ep10",
    permissionPrefix: "rrcep10wastemanifest"
  },
  RRCEP10WASTEMFSTBLDSTATUS: {
    link: undefined,
    permissionPrefix: ""
  },
  SVCEVENT: {
    link: "service-events",
    permissionPrefix: "serviceevent"
  },
  SVCEVENTSERVICETYPE: {
    link: undefined,
    permissionPrefix: ""
  },
  SVCEVENTTGT: {
    link: undefined,
    permissionPrefix: ""
  },
  SVCEVENTTICKETPOLICY: {
    link: "service-event-ticket-policies",
    permissionPrefix: "serviceeventticketpolicy"
  },
  SVCEVENTTICKETSCOPE: {
    link: undefined,
    permissionPrefix: ""
  },
  SERVICETYPEFACET: {
    link: undefined,
    permissionPrefix: ""
  },
  SERVICETYPEFACETMODE: {
    link: undefined,
    permissionPrefix: ""
  },
  SERVICETYPEMODE: {
    link: undefined,
    permissionPrefix: ""
  },
  SVCEVENTTYPESERVICETYPE: {
    link: undefined,
    permissionPrefix: ""
  },
  SVCEVENTTARGET: {
    link: undefined,
    permissionPrefix: ""
  },
  FLEETASSETCAPAB: {
    link: undefined,
    permissionPrefix: ""
  },
  LOCSTATUSFACET: {
    link: undefined,
    permissionPrefix: ""
  },
  NOTIF_SEND_PROF: {
    link: "notifications/sender-profiles",
    permissionPrefix: "notificationsenderprofile"
  },
  NOTIF_SUB: {
    link: undefined,
    permissionPrefix: ""
  },
  NOTIF_TEMPLATE: {
    link: "notifications/templates",
    permissionPrefix: "notificationtemplate"
  },
};

export const getEntityLinkConfig = (type: EntityType | string): EntityLinkConfig => {
  if (!isEntityType(type)) {
    return DEFAULT_ENTITY_LINK_CONFIG;
  }

  return {
    ...DEFAULT_ENTITY_LINK_CONFIG,
    ...ENTITY_LINK_CONFIG_OVERRIDES[type],
  };
}

export const getEntityLink = (entityType: EntityType, id: string) : string | undefined => {
  const config = getEntityLinkConfig(entityType)
  if (config && config.link) {
    return `/${config.link}/profile?id=${id}`
  } else {
    return undefined
  }
}

/** The `<prefix>_view` permission a viewer needs to follow a link to this entity type, if any. */
export const getEntityViewPermission = (type: EntityType | string): string | undefined => {
  const prefix = getEntityLinkConfig(type).permissionPrefix;
  return prefix ? `${prefix}_view` : undefined;
};
